import { route } from "../../shim/jsbridge";
import { isLocaleAvailableForPlatform } from "../../utils";
import { AxiosApiWrapper } from "./AxiosApiWrapper";

const PROFILE_BASED_INTEGRATIONS = [
  "cisco_secure_malware_analytics",
  "fireeye",
  "joe"
];

const isProfileBasedIntegration = integration =>
  PROFILE_BASED_INTEGRATIONS.includes(integration);

const getDownloadBulkRoute = unpacked =>
  `api-download-bulk${unpacked ? "-unpacked" : ""}`;

class GlobalApi extends AxiosApiWrapper {
  /**
   * Get Profile
   */
  retrieveProfile = () => this.get(route("api-user-profile"));

  /**
   * Post Profile
   */
  updateProfile = data => this.patch(route("api-user-profile"), data);

  /**
   * List Subscription
   */
  listSubscriptions = queryParams =>
    this.get(route("api-subscription-group-list", {}, queryParams));

  /**
   * Create Subscription
   */
  createSubscription = data =>
    this.post(route("api-subscription-group-list"), data);

  /**
   * Get Subscription
   */
  retreiveSubscription = id =>
    this.get(route("api-subscription-group-detail", [id]));

  /**
   * Put Subscription
   */
  updateSubscription = (id, data) =>
    this.put(route("api-subscription-group-detail", [id]), data);

  /**
   * Delete Subscription
   */
  destroySubscription = id =>
    this.delete(route("api-subscription-group-detail", [id]));

  /**
   * Remove hashes from subscriptions
   */
  unsubscribeHashes = hashes =>
    this.post(route("api-unsubscribe-hash-values"), { hash_values: hashes });

  /**
   * List Actions
   */
  listActions = queryParams =>
    this.get(route("api-alertaction-list", {}, queryParams));

  /**
   * Create Action
   */
  createAction = data => this.post(route("api-alertaction-list"), data);

  /**
   * Put Action
   */
  updateAction = (id, data) =>
    this.put(route("api-alertaction-detail", [id]), data);

  /**
   * Delete Action
   */
  destroyAction = id => this.delete(route("api-alertaction-detail", [id]));

  /**
   * List Samples
   */
  listSamples = (queryParams, cancelToken) =>
    this.get(route("uploads-search-data", {}, queryParams), { cancelToken });
  /**
   * Search YARA rulesets
   */
  listYaraRulesets = queryParams =>
    this.get(route("api-yara-rulesets", {}, queryParams));

  /**
   * Fetch YARA widget data
   */
  fetchYaraWidgetRulesets = (queryParams, signal) =>
    this.get(route("yara_widget", {}, queryParams), { signal });

  /**
   * List Alerts
   * */
  listAlerts = data => this.get(route("api-alert-list", {}, data));

  /**
   * Resolve Alerts
   */
  resolveAlerts = params => this.post(route("api-alerts-resolve"), params);

  /**
   * Resolve All unresolved Alerts
   */
  resolveAllAlerts = params =>
    this.post(route("api-alerts-resolve-all"), params);

  /**
   * Fetch & Analyze
   * */
  fetchAnalyze = data => this.post(route("fetch_analyze"), data);

  /**
   * Copy to my uploads
   * */
  copySampleToMyUploads = ({ hashValue, filename }) =>
    this.post(route("copy_sample_to_upload_action", [hashValue]), { filename });

  /**
   * Copy uploaded sample to my submissions
   * */
  copyUploadedSampleToMySubmissions = eventId =>
    this.post(route("copy_upload_action", [eventId]), {});

  /**
   * Check Sample Download
   * */
  checkSampleDownload = ({ hash, filename, zip, unpacked }) => {
    const routeName = unpacked
      ? "api-download-unpacked"
      : "download_filename_url";
    return this.head(route(routeName, [hash], { filename, zip }));
  };

  /**
   * Get top container of the sample
   * */
  getTopContainer = ({ hash }) =>
    this.get(route("api-sample-top-container", [hash]));

  /**
   * Delete Uploaded Sample
   * */
  deleteUploadSample = ({ hash }) =>
    this.delete(route("delete_upload_action", [hash]));

  bulkDeleteUploadSamples = ({ hashes }) =>
    this.post(route("delete_upload_bulk_action"), { hashes });

  /**
   * Spectra Core Reanalysis for uploads by sample hash
   * */
  reanalyzeTiCore = ({ hash_value }) =>
    this.get(route("reanalyze_ticore_by_hash_action", { hash_value }));

  /**
   * Spectra Intelligence Analysis
   * */
  ticloudAnalyze = ({ hash_value }) =>
    this.post(route("samples-analyze", { hash_value }), { analysis: "cloud" });

  yaraSaveRule = data => this.post(route("yara_insert"), data);
  yaraUpdateRule = ({ data, id }) =>
    this.post(route("yara_update", {}, { id }), data);

  yaraFetchRule = ({ id }) => this.get(route("yara_details", {}, { id }));

  yaraAction = ({ id, action }) =>
    this.get(route("yara_action", {}, { id, action }));

  reanalyzeSampleStatus = ({ hash_values }) =>
    this.post(route("api-reanalyze-sample-status"), { hash_values }).then(
      data => data
    );

  /**
   * Spectra Intelligence Analysis Status
   * */
  sampleSummaryTiCloudAnalysisStatus = ({ hash_value, signal }) =>
    this.get(
      route("samples-analyze-status", { hash_value }, { analysis: "cloud" }),
      { signal }
    );

  getBulkUserClassification = ({ hashValues }) =>
    this.post(route("api-sample-bulk-userclassification"), {
      hash_values: hashValues
    });

  setBulkUserClassification = ({ hashValues, data }) =>
    this.put(route("api-sample-bulk-userclassification"), {
      hash_values: hashValues,
      data
    });

  deleteBulkUserClassification = ({ hashValues }) =>
    this.delete(route("api-sample-bulk-userclassification"), {
      data: { hash_values: hashValues }
    });

  getUserBulkCloudClassification = ({ hashValues }) =>
    this.post(route("api-sample-bulk-cloud-userclassification"), {
      hash_values: hashValues
    });

  setUserBulkCloudClassification = ({ hashValues, data }) =>
    this.put(route("api-sample-bulk-cloud-userclassification"), {
      hash_values: hashValues,
      data
    });

  deleteUserBulkCloudClassification = ({ hashValues }) =>
    this.delete(route("api-sample-bulk-cloud-userclassification"), {
      data: { hash_values: hashValues }
    });

  /**
   * Add favourite query
   * */
  addFavourite = ({ name, ...rest }) =>
    this.post(route("samples-search-favorites-single", [0]), { name, ...rest });

  /**
   * Delete favourite query
   * */
  deleteFavourite = id =>
    this.delete(route("samples-search-favorites-single", [id]));

  /**
   * Retrieve favourite queries
   * */
  listFavourites = () => this.get(route("samples-search-favorites-list"));

  /**
   * Edit favourite query
   * */
  editFavourite = ({ id, name }) =>
    this.patch(route("samples-search-favorites-single", [id]), {
      name
    });

  /**

   * List search history
   * */
  listSearchHistory = () => this.get(route("samples-search-history-list"));

  /**
   * Share search query
   * */
  shareQuery = params => this.post(route("query-share"), { ...params });

  /**
   * Check if samples are available for download
   * */
  availableForDownload = hash_values =>
    this.post(route("api-available-for-download"), { hash_values });

  /**
   * Create cluster
   */
  createCluster = data => this.post(route("api_cluster_create"), data);

  /**
   * Exchange keys
   */
  exchangeKeysCluster = data =>
    this.post(route("api_cluster_exchange_keys"), data);

  /**
   * Get cluster configuration
   */
  getClusterConfiguration = () => this.get(route("api_cluster_get_config"));

  /**
   * Destroy cluster
   */
  destroyCluster = data => this.post(route("api_cluster_destroy"), data);

  /**
   * Get cluster status
   */
  getClusterStatus = () => this.get(route("api_cluster_get_status"));

  /**
   * Manual switchover
   */
  manualSwitchoverCluster = () => this.post(route("api_cluster_switchover"));

  /**
   * Verify configuration
   */
  verifyConfig = data => this.post(route("api_cluster_verify"), data);

  /**
   * Upgrade wizard initial data (available updates, ticloud state, upload path)
   */
  getUpgradeWizardInit = () =>
    this.get(route("api_cluster_upgrade_wizard_init"));

  /**
   * Upgrade wizard state / trigger upgrade
   */
  upgradeWizard = data => this.post(route("api_cluster_upgrade_wizard"), data);

  /**
   * Queue an update package download from the update repo
   */
  downloadUpdatePackage = data => this.post("/updaterepo/download/", data);

  /**
   * Poll download status for an update package
   */
  getUpdateDownloadStatus = params =>
    this.get("/updaterepo/update-status/", { params });

  /**
   * Upload an update file via the Flow.js chunked-upload endpoint.
   * Uses 1MB chunks (matching update_form.html) for fault-tolerance on large files.
   */
  uploadUpdateFile = ({ file, uuid, onUploadProgress, signal }) => {
    const CHUNK_SIZE = 1024 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadedBytes = 0;

    const uploadChunk = chunkNumber => {
      const start = (chunkNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const formData = new FormData();
      formData.append("file", file.slice(start, end));
      formData.append("csrfmiddlewaretoken", window.tcbase.csrfToken());
      formData.append("flowChunkNumber", String(chunkNumber));
      formData.append("flowChunkSize", String(CHUNK_SIZE));
      formData.append("flowCurrentChunkSize", String(end - start));
      formData.append("flowTotalSize", String(file.size));
      formData.append("flowIdentifier", uuid);
      formData.append("flowFilename", file.name);
      formData.append("flowRelativePath", file.name);
      formData.append("flowTotalChunks", String(totalChunks));
      return this.post("/system/upload/", formData, {
        onUploadProgress: ev => {
          if (ev.total && onUploadProgress)
            onUploadProgress({
              loaded: uploadedBytes + ev.loaded,
              total: file.size
            });
        },
        signal
      }).then(() => {
        uploadedBytes += end - start;
        if (chunkNumber < totalChunks) return uploadChunk(chunkNumber + 1);
      });
    };

    return uploadChunk(1);
  };

  /**
   * Start tailing logs process on backend
   */
  startLogTail = data => this.post(route("api_logtail_start"), data);

  /**
   * Get log records from tails process on backend
   */
  logTail = data => this.post(route("api_logtail"), data);

  /**
   * Stop tailing logs process on backend
   */
  stopLogTail = data => this.post(route("api_logtail_stop"), data);

  /**
   * Init bulk download
   * */
  bulkDownloadInit = (samples, zipPassword, unpacked) =>
    this.post(route(getDownloadBulkRoute(unpacked)), {
      samples,
      zip_password: zipPassword
    });

  /**
   * Check bulk (unpacked or regular) download status
   * */
  bulkDownloadCheck = id =>
    this.get(route("api-download-bulk-status", {}, { id }));

  /**
   * Cancel bulk download
   * */
  bulkDownloadCancel = (id, unpacked) =>
    this.delete(route(getDownloadBulkRoute(unpacked), {}, { id }));

  /**
   * Bulk download
   * */
  bulkDownload = (id, unpacked) =>
    this.get(route(getDownloadBulkRoute(unpacked), { id }));

  /**
   * Bulk hash search
   * */
  bulkHashSearch = ({ content, location, signal }) =>
    this.post(route("bulk-search"), { content, location }, { signal });

  /**
   * Expanded details cloud
   * */
  expandedDetailsCloud = hashValue =>
    this.get(
      route("api-sample-expanded-details-cloud", { hash_value: hashValue })
    );

  /**
   * Expanded details cloud
   * */
  summaryDetailsCloud = hashValue =>
    this.get(
      route("api-sample-summary-details-cloud", { hash_value: hashValue })
    );

  /**
   *  Dashboard uploads total counts
   **/

  uploadsTotalCounts = (queryParams, signal) =>
    this.get(route("uploads_dashboard_total_counts", [], queryParams), {
      signal
    });

  /**
   *  Dashboard classification counts
   **/

  uploadsClassificationCounts = (queryParams, signal) =>
    this.get(
      route("uploads_dashboard_classification_counts", [], queryParams),
      { signal }
    );

  /**
   * Dashboard threat landscape chart data
   */
  threatLandscapeData = signal =>
    this.get(route("threat_landscape_data", [], {}), { signal });

  /**
   *  Dashboard uri table data
   */
  uriTableData = (queryParams, signal) =>
    this.get(route("uri_table_data", [], queryParams), { signal });

  /**
   *  Email widget data
   */
  emailWidgetData = (queryParams, signal) =>
    this.get(route("email_widget_data", [], queryParams), { signal });

  /**
   * Email widget threat names data
   */
  emailWidgetFiltersData = (queryParams, signal) =>
    this.get(route("email_widget_searchable_filters", [], queryParams), {
      signal
    });

  /**
   *  Dashboard uri table threat name data
   */

  uriTableThreatsData = (queryParams, signal) =>
    this.get(route("uri_table_threats", [], queryParams), { signal });

  /**
   * Fetch and analyze uri from uri table
   * @param url
   */
  uriTableFetchAndAnalyze = (
    uri,
    scraper,
    platform,
    geolocation,
    locale,
    timeout,
    interactivePlatform,
    interactiveTimeout,
    interactiveGeolocation,
    interactiveLocale,
    rlDynamicAnalysis,
    rlcsInteractive,
    fileless,
    joe
  ) => {
    const analysis = {};

    if (joe) {
      analysis.joe = {};
    }

    if (rlDynamicAnalysis) {
      const rlDynamicAnalysisConfig = {};

      if (platform) {
        rlDynamicAnalysisConfig.platform = platform;

        if (geolocation) {
          rlDynamicAnalysisConfig.geolocation = geolocation;
        }
        if (locale && isLocaleAvailableForPlatform(platform)) {
          rlDynamicAnalysisConfig.locale = locale;
        }

        if (timeout) {
          rlDynamicAnalysisConfig.timeout = timeout;
        }
      }

      analysis.rl_dynamic_analysis = rlDynamicAnalysisConfig;
    }
    if (rlcsInteractive) {
      const rlInteractiveAnalysisConfig = {};

      if (interactivePlatform) {
        rlInteractiveAnalysisConfig.platform = interactivePlatform;

        if (interactiveTimeout) {
          rlInteractiveAnalysisConfig.timeout = interactiveTimeout;
        }
        if (interactiveGeolocation) {
          rlInteractiveAnalysisConfig.geolocation = interactiveGeolocation;
        }
        if (interactiveLocale) {
          rlInteractiveAnalysisConfig.locale = interactiveLocale;
        }
      }

      analysis.rl_interactive_analysis = rlInteractiveAnalysisConfig;
    }

    return this.post(route("upload_analyze_url"), {
      url: uri,
      crawler: scraper,
      analysis,
      fileless
    });
  };

  /**
   *  Dashboard threat type data
   */

  threatTypeData = (queryParams, signal) =>
    this.get(route("threat_type_data", [], queryParams), { signal });

  /**
   * Expanded details
   * */
  expandedDetailsLocal = (hashValue, sourceId, relationshipId) => {
    const queryParams = {};
    if (sourceId) {
      queryParams.source_id = sourceId;
    }
    if (relationshipId) {
      queryParams.relationship_id = relationshipId;
    }
    return this.get(
      route(
        "api-sample-expanded-details-local",
        { hash_value: hashValue },
        (sourceId || relationshipId) && queryParams
      )
    );
  };

  /**
   * Sample report
   */
  sampleReport = hash_value =>
    this.get(route("api-sample-report", { hash_value }));

  /**
   * Source count
   */
  sourcesCounts = hash_value =>
    this.get(route("sources_counts", { hash_value }));

  /**
   * Return sources data
   */
  sourcesData = (hash_value, signal) =>
    this.get(route("api_sources", { hash_value }), { signal });

  /**
   * Return all uploads data for a particular sample
   */
  uploadsData = (hash_value, signal) =>
    this.get(route("api-samples-uploads-data", [hash_value]), { signal });

  /**
   * Rha1 counters
   */
  rha1Counters = (hash_value, signal) =>
    this.get(route("rha1_counters", { hash_value }), { signal });

  rha1GroupBy = (hash_value, queryParams) =>
    this.get(route("rha1_group_by", { hash_value }, queryParams));

  /**
   * Malware presence
   */
  mwpDetectionCounts = (sha1, signal) =>
    this.post(
      route("api-malware-presence-detection-counts", { hash_value: sha1 }),
      { signal }
    );

  /**
   * Sample exists locally
   */

  sampleExistsLocal = hashValue =>
    this.head(route("samples-exists", { hash_value: hashValue }));

  /**
   * Reanalyze samples with selected systems
   * @param {Object} data - The payload for reanalysis
   * @param {AbortSignal} [signal] - Optional AbortSignal to cancel the request
   * @returns {Promise}
   */
  reanalyze = (data, signal) => this.post(route("reanalyze"), data, { signal });

  /**
   * Prepare reanalyze window
   */

  prepareReanalyze = ({ hashValues, signal, skipRlcs }) =>
    this.post(
      route("prepare_reanalyze_action"),
      { hash_values: hashValues, skip_rlcs: skipRlcs },
      { signal }
    );

  prepareRlcsReanalyze = ({ hashValue }) =>
    this.post(route("prepare_rlcs_reanalyze_action"), {
      hash_value: hashValue
    });

  /**
   * RL Cloud Sandbox config
   */
  updateTitaniumCloudDynamicAnalysisConfig = data =>
    this.patch(route("api_ti_cloud_dynamic_analysis_config"), data);
  testTiCloudDynamicAnalysisConnection = () =>
    this.get(route("api_test_ti_cloud_dynamic_analysis_connection"));

  /**
   * RL Auxiliary Analysis config
   */

  updateRlAuxiliaryAnalysisConfig = data =>
    this.patch(route("api_rl_auxiliary_analysis_config"), data);

  testRlAuxiliaryAnalysisConnection = () =>
    this.get(route("api_test_rl_auxiliary_analysis_connection"));

  /**
   * CAPE config
   */

  getCapeConfig = () => this.get(route("api_cape_config"));

  setCapeConfig = data => this.post(route("api_cape_config"), data);

  updateCapeConfig = data => this.patch(route("api_cape_config"), data);

  validateCapeApi = (api_host, api_port) =>
    this.post(route("api_cape_validate_api"), { api_host, api_port });

  testCapeConnection = () => this.get(route("api_test_cape_connection"));

  validateCapeWeb = (web_host, web_port) =>
    this.post(route("api_cape_validate_web"), { web_host, web_port });

  /**
   *  Cisco Secure Malware Analytics (Threat Grid) config
   */

  getCiscoSecureMalwareAnalyticsConfig = () =>
    this.get(route("api_cisco_secure_malware_analytics_config"));

  setCiscoSecureMalwareAnalyticsConfig = data =>
    this.post(route("api_cisco_secure_malware_analytics_config"), data);

  updateCiscoSecureMalwareAnalyticsConfig = data =>
    this.patch(route("api_cisco_secure_malware_analytics_config"), data);

  testCiscoSecureMalwareAnalyticsConnection = () =>
    this.get(route("api_test_cisco_secure_malware_analytics_connection"));

  /**
   *  Cuckoo config
   */

  getCuckooConfig = () => this.get(route("api_cuckoo_config"));

  setCuckooConfig = data => this.post(route("api_cuckoo_config"), data);

  updateCuckooConfig = data => this.patch(route("api_cuckoo_config"), data);

  validateCuckooApi = (
    api_host,
    api_port,
    api_auth_enabled,
    api_token,
    api_token_set
  ) =>
    this.post(route("api_cuckoo_validate_api"), {
      api_host,
      api_port,
      api_auth_enabled,
      api_token,
      api_token_set
    });

  testCuckooConnection = () => this.get(route("api_test_cuckoo_connection"));

  validateCuckooWeb = (web_host, web_port) =>
    this.post(route("api_cuckoo_validate_web"), { web_host, web_port });

  /**
   *  FireEye config
   */

  getFireEyeConfig = () => this.get(route("api_fireeye_config"));

  setFireEyeConfig = data => this.post(route("api_fireeye_config"), data);

  updateFireEyeConfig = data => this.patch(route("api_fireeye_config"), data);

  validateFireEyeApi = (
    api_host,
    api_port,
    api_prefix,
    username,
    password,
    password_set
  ) =>
    this.post(route("api_fireeye_validate_api"), {
      api_host,
      api_port,
      api_prefix,
      username,
      password,
      password_set
    });

  testFireEyeConnection = () => this.get(route("api_test_fireeye_connection"));

  validateFireEyeWeb = (web_host, web_port) =>
    this.post(route("api_fireeye_validate_web"), {
      web_host,
      web_port
    });

  fetchFireEyeProfiles = () =>
    this.get(route("api_fireeye_fetch_available_profiles"));

  /**
   *  Joe Sandbox config
   */

  getJoeConfig = () => this.get(route("api_joe_config"));

  setJoeConfig = data => this.post(route("api_joe_config"), data);

  updateJoeConfig = data => this.patch(route("api_joe_config"), data);

  validateJoeApi = (api_url, api_key, api_key_set) =>
    this.post(route("api_joe_validate_api"), {
      api_url,
      api_key,
      api_key_set
    });

  testJoeConnection = () => this.get(route("api_test_joe_connection"));

  validateJoeWeb = web_url =>
    this.post(route("api_joe_validate_web"), {
      web_url
    });

  fetchJoeProfiles = () => this.get(route("api_joe_fetch_available_profiles"));

  /**
   *  VMRAY config
   */

  getVmRayConfig = () => this.get(route("api_vmray_tcbase_config"));

  setVmRayConfig = data => this.post(route("api_vmray_tcbase_config"), data);

  updateVmRayConfig = data =>
    this.patch(route("api_vmray_tcbase_config"), data);

  validateVmRayApi = (api_url, api_key, api_key_set) =>
    this.post(route("api_vmray_tcbase_validate_api"), {
      api_url,
      api_key,
      api_key_set
    });

  testVmRayConnection = () =>
    this.get(route("api_test_vmray_tcbase_connection"));

  validateVmRayWeb = web_url =>
    this.post(route("api_vmray_tcbase_validate_web"), {
      web_url
    });

  /**
   * List connectors
   */

  listConnectors = () => this.get(route("api_connectors_list"));

  /**
   * Enable connector
   */

  enableConnector = connectorName =>
    this.post(route("api_start_stop_connector", [connectorName]));

  /**
   * Disable connector
   */

  disableConnector = connectorName =>
    this.delete(route("api_start_stop_connector", [connectorName]));

  /**
   * Get connector config
   */

  getConnectorConfig = connectorName =>
    this.get(route("api_connector_proxy", [connectorName, "v1/config"]));

  /**
   * Get connector config status (is it fully enabled and running)
   */

  getConnectorConfigStatus = (connectorName, signal) =>
    this.get(route("api_connector_config_status", [connectorName]), { signal });

  /**
   * Configure connector
   */

  configureConnector = (connectorName, config) =>
    this.post(
      route("api_connector_proxy", [connectorName, "v1/config"]),
      config
    );

  /**
   * Validate connector connection
   */

  testConnectorConnection = (connectorName, connectionData) =>
    this.post(
      route("api_connector_proxy", [connectorName, "v1/test-connection"]),
      connectionData
    );

  /**
   * Get TC static analysis metadata
   */
  tcAnalysisMetadata = (hashValue, params) => {
    let fields;
    if (Array.isArray(params)) {
      fields = params
        .map(f => `fields=${f}`)
        .reduce((acc, current) => `${acc}&${current}`);
    } else {
      fields = {
        fields: params
      };
    }
    return this.get(route("api-sample-ticore-metadata", [hashValue], fields));
  };

  /**
   * Signatures metadata
   */
  tcAnalysisSignatures = hashValue =>
    this.get(
      route("api-sample-ticore-v2", [hashValue], { fields: "signatures,info" })
    );

  /**
   * Returns the display status of RL Dynamic Sandbox Analysis
   */
  rlSandboxDisplayStatus = (hashValue, isNetwork = false) =>
    this.get(
      route(
        "api_rl_dynamic_analysis_display_status",
        [hashValue],
        isNetwork && {
          isNetwork
        }
      )
    );

  /**
   * Return RL Dynamic Sandbox Analysis Merged Report
   */
  rlSandboxMergedReport = (hash_value, isNetwork = false) =>
    this.get(
      route(
        "api_rl_dynamic_analysis_merged_report",
        [hash_value],
        isNetwork && {
          isNetwork
        }
      )
    );

  rlSandboxMergedReportUrl = url =>
    this.post(route("api_rl_dynamic_analysis_merged_report_url"), { url });

  /**
   * Return RL Dynamic Sandbox Analysis Single Report
   */
  rlSandboxSingleReport = (hash_value, analysis_id, isNetwork = false) =>
    this.get(
      route(
        "api_rl_dynamic_analysis_single_report",
        [hash_value, analysis_id],
        isNetwork && {
          isNetwork
        }
      )
    );

  checkRlSandboxReportResourcesExist = (hashValue, analysisId) =>
    this.get(
      route("api_rl_dynamic_analysis_resource_check", [hashValue, analysisId])
    );

  downloadRlSandboxReportScreenshots = (hashValue, analysisId) =>
    this.head(
      route("api_rl_dynamic_analysis_resource_screenshots", [
        hashValue,
        analysisId
      ]),
      {}
    ).then(() => {
      window.location = route("api_rl_dynamic_analysis_resource_screenshots", [
        hashValue,
        analysisId
      ]);
    });

  downloadRlSandboxReportPcaps = (hashValue, analysisId) =>
    this.head(
      route("api_rl_dynamic_analysis_resource_pcaps", [hashValue, analysisId]),
      {}
    ).then(() => {
      window.location = route("api_rl_dynamic_analysis_resource_pcaps", [
        hashValue,
        analysisId
      ]);
    });

  downloadRlSandboxReportMemStrings = (hashValue, analysisId) =>
    this.head(
      route("api_rl_dynamic_analysis_resource_memstrings", [
        hashValue,
        analysisId
      ]),
      {}
    ).then(() => {
      window.location = route("api_rl_dynamic_analysis_resource_memstrings", [
        hashValue,
        analysisId
      ]);
    });

  /**
   * Return FireEye Analysis Summary Info
   */
  fireeyeSummaryInfo = hash_value =>
    this.get(route("api_fireeye_summary_info", [hash_value]));

  /**
   * Returns the display status of FireEye Analysis
   */
  fireeyeDisplayStatus = hash_value =>
    this.get(route("api_fireeye_display_status", [hash_value]));

  /**
   * Returns whether we display task links for FireEye Analysis
   */
  fireeyeShowLinks = hash_value =>
    this.get(route("api_fireeye_show_links", [hash_value]));

  /**
   * Return VMRay Analysis Summary Info
   */
  vmraySummaryInfo = hash_value =>
    this.get(route("api_vmray_tcbase_summary_info", [hash_value]));

  /**
   * Return VMRay Analysis Behavioral Info
   */
  vmrayBehavioralData = hash_value =>
    this.get(route("api_vmray_tcbase_behavioral_data", [hash_value]));

  /**
   * Return VMRay Analysis Dropped Files
   */
  vmrayDroppedFiles = hash_value =>
    this.get(route("api_vmray_tcbase_dropped_files", [hash_value]));

  /**
   * Return VMRay Analysis Indicators of Compromise
   */
  vmrayIocsData = hash_value =>
    this.get(route("api_vmray_tcbase_iocs", [hash_value]));

  /**
   * Return VMRay Analysis Networking Data
   */
  vmrayNetworkingData = hash_value =>
    this.get(route("api_vmray_tcbase_networking", [hash_value]));

  /**
   * Returns the display status of VMRay Analysis
   */
  vmrayDisplayStatus = hash_value =>
    this.get(route("api_vmray_tcbase_display_status", [hash_value]));

  /**
   * Returns whether we display task links for VMRay Analysis
   */
  vmrayShowLinks = hash_value =>
    this.get(route("api_vmray_tcbase_show_links", [hash_value]));

  /**
   * Return status of RL Dynamic Sandbox Analysis
   */
  rlSandboxAnalysisStatus = hash_values =>
    this.post(route("api_rl_dynamic_analysis_status"), hash_values);

  /**
   * Return System Health Status
   */
  systemHealthStatus = () => this.get(route("status_system_health"));

  /**
   * Return url stats
   */
  uriStats = ({ requestData, signal }) =>
    this.post(route("samples_uri_stats"), { uris: requestData }, signal);

  /**
   * Return TiCard data
   */
  tiCard = threatName => this.get(route("ti-card-get", [threatName]));

  getExtractedFiles = ({ hashValue, extractedFilesQP }, signal) =>
    this.get(route("api-sample-browse-files", [hashValue], extractedFilesQP), {
      signal
    });

  getUrlExtractedFiles = ({ url, extractedFilesQP }) =>
    this.post(route("api-sample-browse-url-files", [url], extractedFilesQP), {
      url
    });

  getExtractedFilesFilterOptions = (hashValue, relationship) =>
    this.get(
      route("api-sample-browse-files-filter-options", [hashValue], {
        relationship
      })
    );

  getExtractedAttachments = ({ hashValue, extractedFilesQP }) =>
    this.get(route("api-email-attachments", [hashValue], extractedFilesQP));

  exportSampleFiles = (hashValue, queryParams) =>
    this.get(route("api-sample-export-files", [hashValue], queryParams));

  getRelationshipPath = ({ hashValue, queryParams }) =>
    this.get(route("relationship_paths", [hashValue], queryParams));

  initDownloadLogs = () => this.post(route("api-download-logs"));
  checkLogs = id =>
    this.head(route("api-download-logs", {}, { download_id: id }));

  validateUploadSample = (
    uploadToTicloud,
    platform,
    geolocation,
    locale,
    internetSimulation,
    timeout,
    sampleName,
    interactivePlatform,
    interactiveSampleName,
    interactiveTimeout,
    interactiveGeolocation,
    interactiveLocale,
    dynamicAnalysis,
    localOnly
  ) => {
    const bodyFormData = new FormData();
    if (uploadToTicloud) {
      bodyFormData.set("send_to_ticloud", uploadToTicloud);
    }
    if (platform) {
      bodyFormData.set("platform", platform);
    }
    if (geolocation) {
      bodyFormData.set("geolocation", geolocation);
    }
    if (locale) {
      bodyFormData.set("locale", locale);
    }
    if (internetSimulation) {
      bodyFormData.set("internet_simulation", internetSimulation);
    }
    if (timeout) {
      bodyFormData.set("timeout", timeout);
    }
    if (sampleName) {
      bodyFormData.set("sample_name", sampleName);
    }
    if (interactivePlatform) {
      bodyFormData.set("interactive_platform", interactivePlatform);
    }
    if (interactiveSampleName) {
      bodyFormData.set("interactive_sample_name", interactiveSampleName);
    }
    if (interactiveTimeout) {
      bodyFormData.set("interactive_timeout", interactiveTimeout);
    }
    if (interactiveGeolocation) {
      bodyFormData.set("interactive_geolocation", interactiveGeolocation);
    }
    if (interactiveLocale) {
      bodyFormData.set("interactive_locale", interactiveLocale);
    }
    if (dynamicAnalysis) {
      bodyFormData.set("dynamic_analysis", dynamicAnalysis);
    }

    bodyFormData.set("local_only", localOnly);

    return this.post(route("upload_validate_ajax_url"), bodyFormData);
  };

  /**
   * Submit file via /api/submit/file/ endpoint with metadata support
   * @param file - file
   * @param password - password for zip files
   * @param uploadToTicloud - whether to upload sample to TiCloud
   * @param rlcsParams - parameters for cloud sandbox analysis
   * @param interactiveParams - parameters for interactive cloud sandbox analysis
   * @param dynamicAnalysis - list of additional dynamic analysis
   * @param sources - custom source tag and origin link for the upload
   * @param onUploadProgress - function that tracks upload progress
   * @param signal - abort controller signal used to stop upload
   */
  submitFile = (
    file,
    password,
    uploadToTicloud,
    rlcsParams,
    interactiveParams,
    dynamicAnalysis,
    localOnly,
    sources,
    onUploadProgress,
    signal
  ) => {
    const bodyFormData = new FormData();
    bodyFormData.append("file", file);
    if (password) {
      bodyFormData.set("archive_password", password);
    }

    const analysis = {};
    analysis.cloud = uploadToTicloud ?? false;
    analysis.local_only = localOnly ?? false;
    if (rlcsParams?.platform) {
      analysis.rl_dynamic_analysis = rlcsParams;
    }
    if (interactiveParams?.platform) {
      analysis.rl_interactive_analysis = interactiveParams;
    }
    if (dynamicAnalysis) {
      dynamicAnalysis.forEach(integration => {
        if (
          integration !== "rl_dynamic_analysis" &&
          integration !== "rl_dynamic_analysis_interactive"
        ) {
          analysis[integration] = isProfileBasedIntegration(integration)
            ? {}
            : true;
        }
      });
    }
    bodyFormData.set("analysis", JSON.stringify(analysis));

    const metadata = {
      source: "ui",
      source_tag: sources?.sourceTag || "",
      file_origin_link: sources?.fileOriginLink || ""
    };
    bodyFormData.set("metadata", JSON.stringify(metadata));

    return this.post(route("api-submit-file"), bodyFormData, {
      onUploadProgress,
      signal
    });
  };

  uploadFromUrl = (
    fileUrl,
    uploadToTicloud,
    rlCloudSandboxPlatform,
    rlCloudSandboxGeolocation,
    rlCloudSandboxLocale,
    rlCloudSandboxExecutionTimeout,
    rlCloudSandboxInternetSimulation,
    rlCloudSandboxSampleName,
    interactivePlatform,
    interactiveExecutionTimeout,
    interactiveSampleName,
    interactiveGeolocation,
    interactiveLocale,
    dynamicAnalysis,
    localOnly,
    sourceTag,
    fileOriginLink
  ) => {
    const bodyFormData = new FormData();
    bodyFormData.set("url", fileUrl);
    if (uploadToTicloud) {
      bodyFormData.set("send_to_ticloud", uploadToTicloud);
    }
    if (rlCloudSandboxPlatform) {
      bodyFormData.set("platform", rlCloudSandboxPlatform);
    }
    if (rlCloudSandboxGeolocation) {
      bodyFormData.set("geolocation", rlCloudSandboxGeolocation);
    }
    if (rlCloudSandboxLocale) {
      bodyFormData.set("locale", rlCloudSandboxLocale);
    }
    if (rlCloudSandboxExecutionTimeout) {
      bodyFormData.set("timeout", rlCloudSandboxExecutionTimeout);
    }
    if (rlCloudSandboxInternetSimulation) {
      bodyFormData.set("internet_simulation", rlCloudSandboxInternetSimulation);
    }
    if (rlCloudSandboxSampleName) {
      bodyFormData.set("sample_name", rlCloudSandboxSampleName);
    }
    if (interactivePlatform) {
      bodyFormData.set("interactive_platform", interactivePlatform);
    }
    if (interactiveExecutionTimeout) {
      bodyFormData.set("interactive_timeout", interactiveExecutionTimeout);
    }
    if (interactiveSampleName) {
      bodyFormData.set("interactive_sample_name", interactiveSampleName);
    }
    if (interactiveGeolocation) {
      bodyFormData.set("interactive_geolocation", interactiveGeolocation);
    }
    if (interactiveLocale) {
      bodyFormData.set("interactive_locale", interactiveLocale);
    }
    if (dynamicAnalysis?.length) {
      bodyFormData.set("dynamic_analysis", dynamicAnalysis?.join(","));
    }
    bodyFormData.set("local_only", localOnly);

    const metadata = {
      source: "ui",
      source_tag: sourceTag || "",
      file_origin_link: fileOriginLink || ""
    };
    bodyFormData.set("metadata", JSON.stringify(metadata));

    return this.post(route("upload_from_url"), bodyFormData);
  };

  createFromUploadYara = (items, sessionId) => {
    const bodyFormData = new FormData();
    bodyFormData.append("items", items);
    bodyFormData.append("uploadSessionId", sessionId);

    return this.post(route("yara_create_from_upload"), bodyFormData);
  };

  postYaraFile = ({ file, onUploadProgress, signal }) => {
    const bodyFormData = new FormData();
    bodyFormData.append("file", file);
    bodyFormData.append("csrfmiddlewaretoken", window.tcbase.csrfToken());
    return this.post(route("yara_upload"), bodyFormData, {
      onUploadProgress,
      signal
    });
  };

  getYaraUploadStatus = ({ uploadSessionId }) =>
    this.get(route("yara_upload_status", {}, { uploadSessionId }));

  integrationsConfigOverview = () =>
    this.get(route("api_integrations_config_overview"));

  integrationsFileTypes = () => this.get(route("api_integrations_filetypes"));

  updateIntegrationsFileTypes = data =>
    this.post(route("api_integrations_filetypes"), data);

  /**
   * RL Cloud Sandbox Export Report API
   */
  requestRlSandboxPdfSummaryCreation = hash =>
    this.put(route("rl_dynamic_analysis_summary_export_create", [hash, "pdf"]));
  checkRlSandboxPdfSummaryCreated = hash =>
    this.get(route("rl_dynamic_analysis_summary_export_check", [hash, "pdf"]));
  downloadRlSandboxPdfSummary = hash =>
    (window.location = route("rl_dynamic_analysis_summary_export_download", [
      hash,
      "pdf"
    ]));

  requestRlSandboxHtmlSummaryCreation = hash =>
    this.put(
      route("rl_dynamic_analysis_summary_export_create", [hash, "html"])
    );
  checkRlSandboxHtmlSummaryCreated = hash =>
    this.get(route("rl_dynamic_analysis_summary_export_check", [hash, "html"]));
  downloadRlSandboxHtmlSummary = hash =>
    (window.location = route("rl_dynamic_analysis_summary_export_download", [
      hash,
      "html"
    ]));

  fetchRoles = () => this.get(route("api-user-roles"));
  fetchRole = ({ roleId }) => this.get(route("api-manage-role", [roleId], {}));
  deleteRole = ({ roleId }) =>
    this.delete(route("api-manage-role", [roleId], {}));
  editRole = ({ roleId, data }) =>
    this.patch(route("api-manage-role", [roleId]), data);
  createRole = data => this.post(route("api-user-roles"), data);
  fetchRolesCategories = () => this.get(route("api-user-roles-categories"));

  toggleExactDates = (toggle = "") =>
    this.get(route("toggle_exact_date", {}, toggle));

  getUserData = () => this.get(route("authentication_user_data"));

  patchUserData = request =>
    this.patch(route("authentication_user_data"), request);

  bulkDeleteAllUploads = data =>
    this.post(route("delete_all_upload_action"), data);

  getUrlDomainRedirect = data =>
    this.post(route("rl_dynamic_analysis_query_url"), data);

  sampleProcessingStatus = hashValue =>
    this.get(route("api-sample-processing-state", [hashValue]));

  getLoginOptions = () => this.get(route("api_login_options"));
  login = data => this.post(route("login"), data);

  resetPasswordRequest = data => this.post(route("password_reset"), data);
  resetPasswordRules = () => this.get(route("api_password_reset_rules"));
  resetPasswordSubmit = (data, params) => {
    const { uidb64, token } = params;
    return this.post(
      route("password_reset_form", {
        uidb64,
        token
      }),
      data
    );
  };

  getAllTags = () => this.get(route("tags_all_tags"));
  getUserTags = hashValue => this.get(route("tags_sample_tags", [hashValue]));
  addUserTags = ({ hash, addedTags }) =>
    this.post(route("tags_sample_tags", [hash]), addedTags);
  deleteUserTags = ({ hash, deletedTags }) =>
    this.post(route("tags_delete_tags", [hash]), deletedTags);
  getSystemTags = hashValue => this.get(route("tags_system_tags", [hashValue]));
  tagMany = data => this.post(route("tags_set_many"), data);
  tagAllSearch = data => this.post(route("tag_all_action"), data);
  tagAllExtracted = (data, hash) =>
    this.post(route("api_sample_tag_all_extracted", [hash]), data);

  getSystemVersion = () => this.get(route("system_version_api"));

  getTiCloudUsage = () => this.get(route("api-quota-usage"));

  getQuotaUsageAlertsConfig = () =>
    this.get(route("quota-usage-alerts-config"));
  postQuotaUsageAlertsConfig = config =>
    this.post(route("quota-usage-alerts-config"), config);

  getImportStatus = ({ src, hashValue, bucket }) =>
    this.get(
      `${route(
        "import_status"
      )}?hash_value=${hashValue}&src=${src}&bucket=${bucket}`
    );

  manageFlexibleIntelFeed = action =>
    this.post(route("api_flexible_intel_feed"), action);

  checkFifPrerequisite = () => this.get(route("check_fif_prerequisite"));

  checkCloudConnection = data =>
    this.post(route("system_check_connection", { connection: "cloud" }), data);

  getQuotaExceededForProduct = productName =>
    this.get(route("quota-exceeded-for-product"), {
      params: { product_name: productName }
    });

  // Certificates
  listCertificates = () => this.get(route("api-list-certificates"));

  addCertificate = formData =>
    this.post(route("api-add-certificate"), formData);

  removeCertificate = certificate_identifier =>
    this.post(route("api-remove-certificate"), { certificate_identifier });

  distrustCertificate = certificate_identifier =>
    this.post(route("api-distrust-certificate"), { certificate_identifier });

  trustCertificate = certificate_identifier =>
    this.post(route("api-trust-certificate"), { certificate_identifier });

  removeAddedCertificates = () =>
    this.post(route("api-remove-added-certificates"));

  apiMediaaddress = () => this.get(route("api-mediaaddress"));

  updatePreferredUI = preferredUi =>
    this.post(route("api-set-preferred-ui"), { preferred_ui: preferredUi });
}

const api = new GlobalApi();

export default api;
